import React, {Component} from 'react';
import PropTypes from 'prop-types';
import CommonAppSetup from '../../common/common-app-setup/CommonAppSetup';
import PopOverPlacement from './PopOverPlacement';
import keycode from 'keycode';
import Components from '../../common/Components';
import joinClassNames from "../../common/JoinClassNames";
import popOverMode from './PopOverMode';
import popOverPosition from './PopOverPosition';

const propTypes = {
	// the element(s) to be hovered to trigger the popOver
	anchor: PropTypes.any.isRequired,

	// the size in px of the arrow for the popup (used to calculate positioning)
	arrowSize: PropTypes.number,

	// the element(s) to show in the popOver after the anchor is hovered
	children: PropTypes.any,

	// css classes to add to the popover wrapper element
	className: PropTypes.string,

	// css classes to add to the popover__popup wrapper
	classNameAnchor: PropTypes.string,

	// css classes to add to the popover__popup wrapper
	classNamePopup: PropTypes.string,

	// The next click outside a click-mode popup will close it
	dismissible: PropTypes.bool,

	// If true, adds tabindex="0" to wrapper (Avoid using this when possible; favor natural keyboard-focusable elements)
	focusable: PropTypes.bool,

	// max pixel width of the popup
	maxWidth: PropTypes.number,

	// mode of hover vs click for working with the popover
	mode: PropTypes.oneOf(Object.values(popOverMode)),

	// respond to popover show/hide changes (switches PopOver to "controlled" mode and a showPopup value must be passed in!); This is useful if the parent component wants to help tell the PopOver when to show/hide
	onPopoverToggle: PropTypes.func,

	// where to show the popover
	position: PropTypes.oneOf(Object.values(popOverPosition)),

	// should the popup be shown; pass undefined to let the PopOver determine this by itself (switches PopOver to "controlled" mode and a onPopoverToggle value must be passed in!); This is useful if the parent component wants to help tell the PopOver when to show/hide
	showPopup: PropTypes.bool,
};

const defaultProps = {
	arrowSize: 12,
	children: undefined,
	className: undefined,
	classNameAnchor: undefined,
	classNamePopup: undefined,
	dismissible: false,
	focusable: false,
	maxWidth: 420,
	mode: popOverMode.HOVER,
	onPopoverToggle: undefined,
	position: popOverPosition.TOP,
	showPopup: undefined,
};

export default class PopOver extends Component {

	constructor(props) {
		super(props);

		this.state = {
			popupIsVisible: false,
			arrowPosition: undefined,
			popOverStyle: undefined,
		};

		this.intervalId = undefined;
		this.lastClientY = undefined;
		this.lastClientX = undefined;

		this.popupRef = React.createRef();
		this.wrapperRef = React.createRef();
		this.arrowRef = React.createRef();

		this.hidePopup = this.hidePopup.bind(this);
		this.showPopup = this.showPopup.bind(this);
		this.isVisible = this.isVisible.bind(this);

		this.handleDismissible = this.handleDismissible.bind(this);
		this.handleEsc = this.handleEsc.bind(this);

		this.updateFromWrapperRect = this.updateFromWrapperRect	.bind(this);

		this.popOverPlacement = new PopOverPlacement(this.props.arrowSize);
		this.currentAriaId = undefined;

		if ((this.props.onPopoverToggle === undefined) !== (this.props.showPopup === undefined)) {
			console.error('PopOver: Either use onPopoverToggle and showPopup or use neither. There is no middle ground since using them changes PopOver\'s popupIsVisible state to be controlled')
		}
	}

	// ==== React Events ==== //
	componentDidUpdate() {
		this.addRemoveEventHandlers(this.isVisible());

		// start/stop update timer
		if (this.isVisible()) {
			if (this.intervalId === undefined) {
				this.updateFromWrapperRect();
				this.intervalId = window.setInterval(this.updateFromWrapperRect, 100);
			}
		} else if (this.intervalId !== undefined) {
			window.clearInterval(this.intervalId);
			this.intervalId = undefined;
		}
	}

	componentWillUnmount() {
		this.addRemoveEventHandlers(false);
	}

	addRemoveEventHandlers(add) {
		if (add) {
			// Helps with weird offsets when content is shifted
			window.addEventListener('resize', this.hidePopup);
			document.addEventListener('keydown', this.handleEsc);

			if (this.props.mode === popOverMode.CLICK && this.props.dismissible) {
				document.addEventListener('click', this.handleDismissible);
			}
		} else {
			window.removeEventListener('resize', this.hidePopup);
			document.removeEventListener('keydown', this.handleEsc);
			document.removeEventListener('click', this.handleDismissible);
			if (this.intervalId) {
				window.clearInterval(this.intervalId);
				this.intervalId = undefined;
			}
		}
	}


	// ==== Window Sizing Events ==== //
	updateFromWrapperRect() {
		const wrapperRect = this.wrapperRef.current.getBoundingClientRect();
		const clientY = wrapperRect.top;
		const clientX = wrapperRect.left;

		if (!this.lastClientY || clientY !== this.lastClientY || clientX !== this.lastClientX) {
			this.lastClientY = clientY;
			this.lastClientX = clientX;
			if (this.isVisible()) {
				this.setState({
					popOverStyle: this.popOverPlacement.updatePopupOffsets(
						this.props.position,
						this.wrapperRef.current.getBoundingClientRect(),
						this.popupRef.current.getBoundingClientRect(),
						this.arrowRef.current.getBoundingClientRect()
					),
				});
			}
		}
	}


	// ==== UI Events ==== //
	handleDismissible(e) {
		if (this.isVisible() && !this.popupRef.current.contains(e.target)) {
			this.hidePopup();
		}
	}

	handleEsc(e) {
		if (keycode.isEventKey(e, keycode.codes.esc)) {
			this.hidePopup();
		}
	}


	// === Hide/Show popup ==== //
	hidePopup() {
		if (this.props.onPopoverToggle) {
			this.props.onPopoverToggle(false);
		} else {
			this.setState({popupIsVisible: false});
		}
	}

	showPopup() {
		if (this.props.onPopoverToggle) {
			this.props.onPopoverToggle(true);
		} else {
			this.setState({popupIsVisible: true});
		}
	}

	togglePopup() {
		this.isVisible() ? this.hidePopup() : this.showPopup();
	}

	isVisible() {
		return this.props.showPopup === undefined ? this.state.popupIsVisible : this.props.showPopup;
	}

	ariaId() {
		if (this.isVisible()) {
			this.currentAriaId = this.currentAriaId || 'tt-' + Date.now();
		} else {
			this.currentAriaId = '';
		}
		return this.currentAriaId;
	}

	// ==== Render ==== ///
	render() {
		const handleMouseEnter = this.props.mode === popOverMode.HOVER ? this.showPopup : undefined;
		const handleMouseLeave = this.props.mode === popOverMode.HOVER ? this.hidePopup : undefined;
		const popOverPositionModifier = this.state.popOverStyle ? `--${this.state.popOverStyle.position}` : '';
		return (
			<div className={joinClassNames(
				'popover',
				this.props.className,
				CommonAppSetup.getClassName(Components.POP_OVER, 'wrapper')
			)}>
				<div
					key="PopOverAnchor"
					className={joinClassNames(
						'popover__anchor',
						this.props.mode,
						this.props.classNameAnchor,
						CommonAppSetup.getClassName(Components.POP_OVER, 'anchorWrapper')
					)}
					ref={this.wrapperRef}
					aria-describedby={this.ariaId()}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					onFocus={handleMouseEnter}
					onBlur={handleMouseLeave}
					onClick={() => this.props.mode === popOverMode.CLICK && this.togglePopup()}
					onKeyDown={this.handleEsc}
				>
					{this.props.anchor}
				</div>

				{this.isVisible() ? (
					<div
						key="PopOverPopup"
						className={joinClassNames(
							this.props.classNamePopup,
							'popover__popup',
							`popover__popup${popOverPositionModifier}`,
							CommonAppSetup.getClassName(Components.POP_OVER, 'popupWrapper')
						)}
						ref={this.popupRef}
						role="popover"
						id={this.ariaId()}
						style={Object.assign({maxWidth: `${this.props.maxWidth}px`}, this.state.popOverStyle ? this.state.popOverStyle.popupStyle : {})}
						tabIndex={this.props.focusable ? 0 : undefined}
					>
						{this.props.children}
						<div
							style={this.state.popOverStyle ? this.state.popOverStyle.arrowStyle : {}}
							className={joinClassNames(
								'popover__arrow',
								`popover__arrow${popOverPositionModifier}`,
								CommonAppSetup.getClassName(Components.POP_OVER, 'arrow')
							)}
							ref={this.arrowRef}
						/>
					</div>
				) : undefined}
			</div>
		);
	}
}

PopOver.MODE = popOverMode;
PopOver.POSITION = popOverPosition;
PopOver.defaultProps = defaultProps;
PopOver.propTypes = propTypes;
